import { Helmet } from "react-helmet";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SkipToContent from "@/components/SkipToContent";
import { 
  Target, 
  Compass, 
  Heart, 
  Lightbulb, 
  Shield, 
  Users, 
  Sprout 
} from "lucide-react";

const About = () => {
  const managementCommittee = [
    { name: "CPA James Kilonzi", title: "Chairman" },
    { name: "CPA Amos Manani, OGW", title: "Vice Chairman" },
    { name: "CPA Marion Mwende", title: "Hon. Treasurer" },
    { name: "Mr. Paul Kariuki, CHRP", title: "Hon. Secretary" },
    { name: "Ms. Pauline Njugu", title: "Board Member" },
    { name: "CPA Belita Musangi", title: "Board Member" },
    { name: "Mr. Jim Mushira, CHRP", title: "Board Member" },
    { name: "Eng. Isaiah Achini", title: "Board Member" },
    { name: "Mr. Enock Mayaka", title: "Board Member" },
  ];

  const supervisoryCommittee = [
    { name: "Mr. Samson Mulondo", title: "Board Member" },
    { name: "Mr. Bernard Okeyo", title: "Board Member" },
    { name: "Mr. David Gregory", title: "Board Member" },
  ];

  const coreValues = [
    {
      icon: Sprout,
      title: "Sustainability",
      description: "We build for the long-term, ensuring the financial health and stability of the Sacco for generations to come."
    },
    {
      icon: Shield,
      title: "Integrity",
      description: "We operate with unwavering honesty and transparency in all our dealings with members and stakeholders."
    },
    {
      icon: Lightbulb,
      title: "Innovation",
      description: "We embrace technology and new ideas to continuously improve our products, services, and member experience."
    },
    {
      icon: Heart,
      title: "Inclusivity",
      description: "We foster a welcoming environment where every member feels valued, heard, and empowered."
    },
    {
      icon: Users,
      title: "Member Focus",
      description: "Our members are at the heart of everything we do. Their needs guide our decisions and their success is our success."
    }
  ];

  return (
    <>
      <Helmet>
        <title>About Natcon Sacco | A Trusted Partner for Financial Growth</title>
        <meta 
          name="description" 
          content="Learn about Natcon Sacco's history, vision, and the dedicated leadership team committed to empowering our members through affordable and innovative financial solutions." 
        />
        <meta 
          name="keywords" 
          content="Natcon Sacco, Kenyan Sacco, savings and credit cooperative, Sacco for National Construction Authority, financial growth Kenya" 
        />
        <link rel="canonical" href="https://natconsacco.or.ke/about" />
      </Helmet>

      <SkipToContent />

      <div className="min-h-screen">
        <Header />
        
        <main id="main-content" tabIndex={-1} className="focus:outline-none">
          {/* Page Header and Introduction */}
          <section className="bg-gradient-hero py-16">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto text-center">
                <h1 className="text-4xl md:text-5xl font-bold text-primary-foreground mb-6">
                  About Natcon Sacco: Our Story, Your Future
                </h1>
                <div className="bg-card/95 backdrop-blur-sm rounded-lg p-8 shadow-strong">
                  <p className="text-lg text-card-foreground leading-relaxed">
                    Founded in 2014, NATCON STAFF SACCO was born from a simple yet powerful idea: to cultivate a savings culture and empower the staff of the National Construction Authority (NCA) to achieve financial independence. From our humble beginnings with just 30 members and KES 443,000 in savings, we have grown into a thriving community of over 258 members. Headquartered in Nairobi and serving our members across the country through robust digital channels, our mission has remained the same: to provide a trusted platform for savings and affordable credit that promotes the financial growth and well-being of every member.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Our Purpose Section */}
          <section className="py-16 bg-background">
            <div className="container mx-auto px-4">
              <div className="grid md:grid-cols-3 gap-8 mb-16">
                {/* Vision */}
                <Card className="text-center shadow-soft hover:shadow-medium transition-shadow">
                  <CardContent className="p-8">
                    <Target className="w-12 h-12 text-primary mx-auto mb-4" />
                    <h2 className="text-2xl font-bold text-card-foreground mb-4">Our Vision</h2>
                    <p className="text-muted-foreground">
                      To be the leading Sacco in promoting members' financial growth and well-being.
                    </p>
                  </CardContent>
                </Card>

                {/* Mission */}
                <Card className="text-center shadow-soft hover:shadow-medium transition-shadow">
                  <CardContent className="p-8">
                    <Compass className="w-12 h-12 text-trust-blue mx-auto mb-4" />
                    <h2 className="text-2xl font-bold text-card-foreground mb-4">Our Mission</h2>
                    <p className="text-muted-foreground">
                      To provide members with high-quality, affordable, and diversified financial services for socioeconomic development.
                    </p>
                  </CardContent>
                </Card>

                {/* Values Intro */}
                <Card className="text-center shadow-soft hover:shadow-medium transition-shadow">
                  <CardContent className="p-8">
                    <Heart className="w-12 h-12 text-accent mx-auto mb-4" />
                    <h2 className="text-2xl font-bold text-card-foreground mb-4">Our Values</h2>
                    <p className="text-muted-foreground">
                      SIIIM principles guide everything we do, ensuring we serve our members with excellence.
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Core Values Grid */}
              <div className="max-w-6xl mx-auto">
                <h2 className="text-3xl font-bold text-center text-foreground mb-12">
                  Our Guiding Principles
                </h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
                  {coreValues.map((value, index) => (
                    <Card key={index} className="text-center shadow-soft hover:shadow-medium transition-all hover:-translate-y-1">
                      <CardContent className="p-6">
                        <value.icon className="w-10 h-10 text-primary mx-auto mb-4" />
                        <h3 className="text-lg font-semibold text-card-foreground mb-3">
                          {value.title}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {value.description}
                        </p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Leadership Section */}
          <section className="py-16 bg-secondary/30">
            <div className="container mx-auto px-4">
              <div className="max-w-6xl mx-auto">
                <h2 className="text-3xl font-bold text-center text-foreground mb-12">
                  Meet the Team Steering Our Vision
                </h2>

                {/* Management Committee */}
                <div className="mb-12">
                  <h3 className="text-2xl font-semibold text-foreground mb-8 text-center">
                    Management Committee
                  </h3>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {managementCommittee.map((member, index) => (
                      <Card key={index} className="shadow-soft hover:shadow-medium transition-shadow">
                        <CardContent className="p-6 text-center">
                          <div className="w-20 h-20 bg-gradient-primary rounded-full mx-auto mb-4 flex items-center justify-center">
                            <Users className="w-10 h-10 text-primary-foreground" />
                          </div>
                          <h4 className="text-lg font-semibold text-card-foreground mb-2">
                            {member.name}
                          </h4>
                          <p className="text-primary font-medium">
                            {member.title}
                          </p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>

                {/* Supervisory Committee */}
                <div>
                  <h3 className="text-2xl font-semibold text-foreground mb-8 text-center">
                    Supervisory Committee
                  </h3>
                  <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
                    {supervisoryCommittee.map((member, index) => (
                      <Card key={index} className="shadow-soft hover:shadow-medium transition-shadow">
                        <CardContent className="p-6 text-center">
                          <div className="w-20 h-20 bg-gradient-primary rounded-full mx-auto mb-4 flex items-center justify-center">
                            <Shield className="w-10 h-10 text-primary-foreground" />
                          </div>
                          <h4 className="text-lg font-semibold text-card-foreground mb-2">
                            {member.name}
                          </h4>
                          <p className="text-primary font-medium">
                            {member.title}
                          </p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Call-to-Action Section */}
          <section className="py-16 bg-gradient-hero">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto text-center">
                <h2 className="text-3xl md:text-4xl font-bold text-primary-foreground mb-6">
                  Ready to Build Your Financial Future with Us?
                </h2>
                <p className="text-xl text-primary-foreground/90 mb-8">
                  Join a growing community dedicated to mutual growth and financial well-being. Explore our products or start your membership application today.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button 
                    size="lg" 
                    className="bg-card text-card-foreground hover:bg-card/90 font-semibold px-8 py-3"
                    asChild
                  >
                    <a href="/contact">Become a Member</a>
                  </Button>
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary font-semibold px-8 py-3"
                    asChild
                  >
                    <a href="/products">Explore Our Products</a>
                  </Button>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default About;